import React, { useState, useRef, useEffect } from 'react';
import type { ChatInputProps } from './types';
import './ChatInput.css';

/**
 * ChatInput Component
 * Input field with send button and suggested questions
 */
// Category display names mapping
const categoryDisplayNames: Record<string, string> = {
  opportunities: 'Opportunities',
  lineItems: 'Line Items',
  accounts: 'Accounts',
  orders: 'Orders',
};

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  suggestedQuestions = [],
  isLoading = false,
  placeholder = 'Ask a question...',
  activeCategory = null,
  categoryQuestions = {},
  onClearCategory,
}) => {
  const [message, setMessage] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState<boolean>(true);
  const [showCategoryQuestions, setShowCategoryQuestions] = useState<boolean>(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Get category questions for active category
  const activeCategoryQuestions = activeCategory ? categoryQuestions[activeCategory] || [] : [];

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // Reset showSuggestions when suggestedQuestions change
  useEffect(() => {
    if (suggestedQuestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [suggestedQuestions]);

  // Reset showCategoryQuestions when activeCategory changes
  useEffect(() => {
    if (activeCategory) {
      setShowCategoryQuestions(true);
    }
  }, [activeCategory]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (message.trim() && !isLoading) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const form = e.currentTarget.form;
      if (form) {
        form.requestSubmit();
      }
    }
  };

  const handleSuggestedClick = (question: string): void => {
    if (!isLoading) {
      onSendMessage(question);
    }
  };

  const handleCategoryQuestionClick = (question: string): void => {
    if (!isLoading) {
      onSendMessage(question);
      // Clear category after sending question
      if (onClearCategory) {
        onClearCategory();
      }
    }
  };

  const handleCloseCategoryQuestions = (): void => {
    setShowCategoryQuestions(false);
    if (onClearCategory) {
      onClearCategory();
    }
  };

  return (
    <div className="chat-input-container">
      {/* Category Questions - shown when a category is selected from top bar */}
      {activeCategory && activeCategoryQuestions.length > 0 && showCategoryQuestions && (
        <div className="suggested-questions category-questions">
          <div className="suggested-header">
            <div className="suggested-label">
              <svg className="suggested-bulb-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                <path d="M9 18h6M10 22h4M15 8a5 5 0 1 0-6 0c0 2 1 3 1 5h4c0-2 1-3 1-5z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Suggested questions: <span className="category-name">{categoryDisplayNames[activeCategory] || activeCategory}</span>
            </div>
            <button
              className="close-suggestions"
              onClick={handleCloseCategoryQuestions}
              type="button"
              title="Close suggestions"
            >
              ✕
            </button>
          </div>
          <div className="suggested-list">
            {activeCategoryQuestions.map((question, idx) => (
              <button
                key={idx}
                className="suggested-button"
                onClick={() => handleCategoryQuestionClick(question)}
                disabled={isLoading}
                type="button"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Suggested Questions from API - shown when no category is active */}
      {!activeCategory && suggestedQuestions.length > 0 && showSuggestions && (
        <div className="suggested-questions">
          <div className="suggested-header">
            <div className="suggested-label">
              <svg className="suggested-bulb-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                <path d="M9 18h6M10 22h4M15 8a5 5 0 1 0-6 0c0 2 1 3 1 5h4c0-2 1-3 1-5z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Suggested questions:
            </div>
            <button
              className="close-suggestions"
              onClick={() => setShowSuggestions(false)}
              type="button"
              title="Close suggestions"
            >
              ✕
            </button>
          </div>
          <div className="suggested-list">
            {suggestedQuestions.map((question, idx) => (
              <button
                key={idx}
                className="suggested-button"
                onClick={() => handleSuggestedClick(question)}
                disabled={isLoading}
                type="button"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="chat-input-form">
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isLoading}
            className="chat-textarea"
            rows={1}
          />
          <div className="input-hint-icon" title="Press Enter to send, Shift+Enter for new line">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" strokeWidth="2"/>
              <path d="M12 16v-4M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <button
            type="submit"
            disabled={!message.trim() || isLoading}
            className="send-button"
          >
            {isLoading ? (
              <svg className="spinner" width="24" height="24" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.415" strokeDashoffset="10" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
